import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import i18n from '@/i18n';

export type ToastType = 'success' | 'error' | 'warning';

export interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

const TOAST_TIMEOUT_MS = 5000;

interface ToastOptions {
  /** Treat the message as an i18n key (default true). */
  translate?: boolean;
  /** Keep the toast until the user closes it (default: true for errors, false otherwise). */
  sticky?: boolean;
}

/** Global spinner + toasts (replaces the Vuex root state, spinner and toaster plugins). */
export const useUiStore = defineStore('ui', () => {
  const pending = ref(0);
  const toasts = ref<Toast[]>([]);
  let nextId = 1;

  const busy = computed(() => pending.value > 0);

  function showSpinner(): void {
    pending.value++;
  }

  function hideSpinner(): void {
    pending.value = Math.max(0, pending.value - 1);
  }

  /** Shows the global spinner for the duration of `work`, whatever its outcome. */
  async function withSpinner<T>(work: () => Promise<T>): Promise<T> {
    showSpinner();
    try {
      return await work();
    } finally {
      hideSpinner();
    }
  }

  function removeToast(id: number): void {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  }

  function toast(type: ToastType, message: string, options: ToastOptions = {}): void {
    const text = options.translate === false ? message : i18n.global.t(message);
    const id = nextId++;
    toasts.value.push({ id, type, message: text });
    const sticky = options.sticky ?? type === 'error';
    if (!sticky) {
      window.setTimeout(() => removeToast(id), TOAST_TIMEOUT_MS);
    }
  }

  return {
    busy,
    toasts,
    showSpinner,
    hideSpinner,
    withSpinner,
    removeToast,
    success: (message: string, options?: ToastOptions) => toast('success', message, options),
    error: (message: string, options?: ToastOptions) => toast('error', message, options),
    warning: (message: string, options?: ToastOptions) => toast('warning', message, options),
  };
});
